import fplToken from '@figma/fpl-tokens/css-vars';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Autocomplete } from '@/UI/Autocomplete/Autocomplete';
import { Box } from '@/UI/Box/Box';
import { Dropdown, type Option } from '@/UI/Dropdown/Dropdown';
import { Input } from '@/UI/Input/Input';
import { Typography } from '@/UI/AppTypography/AppTypography';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { color } from '@/colors';
import { FlexCol } from '@/UI/styles';
import LoRA from '../../Nodes/MultiLoRA/LoRA';
import type { ImageFile } from 'web';

export interface Lora {
  id: string;
  name: string;
  file?: string;
  coverImage?: ImageFile;
  defaultWeight: number;
  trigger: string;
  url?: string;
}

interface MultiLoraValue {
  selectedLora: Lora | null;
  weight: number;
}

type HandleChangeFunction = (paramId: string, newValue: any) => void;

interface ValidationProps {
  error?: boolean;
  helperText?: string | null;
}

interface LoraSelectProps {
  node?: any;
  param: any;
  handleChange: HandleChangeFunction;
  validationProps: ValidationProps;
  disabled: boolean;
}

/**
 * The LoRA picker, split at the caller by the FPL migration flag. Flag-off keeps the legacy MUI
 * `Autocomplete` (rich LoRA rows + search) verbatim; flag-on renders a plain single-select FPL
 * `Dropdown` that shows only the LoRA name in the trigger and each option.
 */
export const LoraSelect = ({ node, param, handleChange, validationProps, disabled }: LoraSelectProps) => {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const value = param.value as MultiLoraValue;
  const loras: Lora[] =
    node?.data?.loras && node.data.loras.length > 0 ? node.data.loras.filter((l: any) => l.file) : [];

  if (isFplMigrationEnabled) {
    const options: Option<Lora>[] = loras.map((lora) => ({
      id: lora.id,
      value: lora,
      label: lora.name,
    }));
    const selectedOption = options.find((o) => o.value.id === value.selectedLora?.id) ?? null;
    const { error, helperText } = validationProps ?? {};
    return (
      <FlexCol sx={{ mt: 1, gap: 0.5 }}>
        <Box
          sx={
            error
              ? {
                  borderRadius: fplToken.radius.medium,
                  outline: `1px solid ${fplToken.color.borderDanger}`,
                  backgroundColor: fplToken.color.bgDangerTertiary,
                  outlineOffset: '-1px',
                }
              : undefined
          }
        >
          <Dropdown<Lora, Option<Lora>>
            width="100%"
            value={selectedOption}
            options={options}
            onChange={(option) =>
              handleChange(node?.id || param.id, { selectedLora: option.value, weight: value.weight })
            }
            disabled={disabled}
          />
        </Box>
        {helperText ? (
          <Typography variant="body-xs-rg" color={error ? fplToken.color.textDanger : token.textSecondary}>
            {helperText}
          </Typography>
        ) : null}
      </FlexCol>
    );
  }

  return (
    <Autocomplete
      id="lora-select"
      sx={{ width: '100%', mt: 1, backgroundColor: 'transparent' }}
      options={loras}
      autoHighlight
      value={value.selectedLora}
      onChange={(event, newValue) => {
        handleChange(node?.id || param.id, {
          selectedLora: newValue,
          weight: value.weight,
        });
      }}
      size="small"
      autoComplete={false}
      getOptionLabel={(option: any) => option.name}
      getOptionKey={(option: any) => option.id}
      isOptionEqualToValue={(option: any, val: any) => option.id === val.id}
      renderOption={(props, option: any) => {
        const { key: _, ...optionProps } = props;
        return (
          <Box
            key={option.id}
            component="li"
            {...optionProps}
            sx={{
              width: '100%',
              backgroundColor: color.Yambo_Blue,
              '&:hover': {
                backgroundColor: color.Yambo_Blue_Stroke,
              },
            }}
          >
            <LoRA
              lora={option}
              container="node"
              updateLora={() => {}}
              deleteLora={() => {}}
              setSelectedLora={() => {}}
            />
          </Box>
        );
      }}
      renderInput={(params) => {
        const { ref: fieldRef, value: inputValue, onChange, onFocus, onBlur, ...restInputProps } = params.inputProps;
        return (
          <Box ref={params.InputProps.ref} sx={{ '& .MuiAutocomplete-endAdornment': { right: 9 } }}>
            <Input
              id={params.id}
              fullWidth={params.fullWidth}
              disabled={params.disabled}
              startAdornment={params.InputProps.startAdornment}
              endAdornment={params.InputProps.endAdornment}
              inputRef={fieldRef}
              value={inputValue}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              inputProps={{
                ...restInputProps,
                autoComplete: 'new-password', // disable autocomplete and autofill
              }}
              error={validationProps?.error}
              helperText={validationProps?.helperText || undefined}
              noAutoSelect
              noBlurOnEnter
            />
          </Box>
        );
      }}
      disabled={disabled}
    />
  );
};
